import React, { useRef, useState } from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2, XCircle, ArrowUpRight, Sparkles } from "lucide-react";
import emailjs from "@emailjs/browser";
import { useParallaxY } from "../../hooks/useParallaxY";
import AnimatedText from "../AnimatedText";
import CreativeButton from "../CreativeButton";

const FloatingField: React.FC<{
  id: string;
  label: string;
  name: string;
  type?: string;
  textarea?: boolean;
  rows?: number;
  value?: string;
  onChange?: (value: string) => void;
}> = ({ id, label, name, type = "text", textarea, rows, value, onChange }) => {
  const sharedClassName =
    "peer w-full px-6 pt-6 pb-2 bg-slate-50/80 dark:bg-[#121212] border border-slate-200 dark:border-white/10 focus:border-[#172b81]/40 dark:focus:border-[#e1ae48]/40 focus:bg-white dark:focus:bg-[#161616] focus:shadow-[0_0_0_4px_rgba(23,43,129,0.06)] dark:focus:shadow-[0_0_0_4px_rgba(225,174,72,0.08)] rounded-2xl outline-none transition-all dark:text-white resize-none";
  const labelClassName =
    "absolute left-6 top-4 text-slate-400 dark:text-slate-500 text-base transition-all duration-200 pointer-events-none peer-focus:top-2.5 peer-focus:text-[11px] peer-focus:text-[#172b81] dark:peer-focus:text-[#e1ae48] peer-[:not(:placeholder-shown)]:top-2.5 peer-[:not(:placeholder-shown)]:text-[11px]";
  const controlled = value !== undefined ? { value, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange?.(e.target.value) } : {};

  return (
    <div className="relative">
      {textarea ? (
        <textarea id={id} name={name} rows={rows ?? 4} placeholder=" " className={sharedClassName} {...controlled} />
      ) : (
        <input id={id} name={name} type={type} placeholder=" " className={sharedClassName} {...controlled} />
      )}
      <label htmlFor={id} className={labelClassName}>{label}</label>
    </div>
  );
};

const phoneLines = [
  { dept: "Operations", text: "+63 977 836 1411", href: "tel:+639778361411" },
  { dept: "Marketing", text: "+63 977 803 1411", href: "tel:+639778031411" },
  { dept: "Sales", text: "+63 977 802 1411", href: "tel:+639778021411" },
];

const emailLines = [
  { dept: "Operations", text: "operations@rkandkimport.com", href: "mailto:operations@rkandkimport.com" },
  { dept: "Marketing", text: "marketing@rkandkimport.com", href: "mailto:marketing@rkandkimport.com" },
  { dept: "Sales", text: "sales@rkandkimport.com", href: "mailto:sales@rkandkimport.com" },
];

const ADDRESS = "#1411 Cavite St., Manila, Philippines";
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;

const topics = ["Request a quote", "Product inquiry", "After-sales support", "Partnership"];

const cardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } },
};

const ContactSection: React.FC = () => {
  const contactSectionRef = useRef<HTMLElement>(null);
  const contactBlobY1 = useParallaxY(contactSectionRef, [-80, 80]);
  const contactBlobY2 = useParallaxY(contactSectionRef, [80, -80]);
  const prefersReducedMotion = useReducedMotion();

  // --- Contact Form Logic (EmailJS) ---
  const form = useRef<HTMLFormElement>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusOk, setStatusOk] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subject, setSubject] = useState('');

  const handleContactSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatusOk(null);
    setStatusMessage('Sending your message...');

    const COOLDOWN_DURATION = 300000;
    const lastSubmissionTime = localStorage.getItem('lastSubmissionTime');
    const currentTime = Date.now();

    if (lastSubmissionTime && currentTime - parseInt(lastSubmissionTime) < COOLDOWN_DURATION) {
      setStatusMessage('You have already sent an inquiry. Please try again later.');
      setStatusOk(false);
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData(event.currentTarget);
    const name = formData.get('user_name') as string;
    const email = formData.get('user_email') as string;
    const subject = formData.get('subject') as string;
    const message = formData.get('message') as string;

    const serviceID = 'service_hbffgwi';
    const publicKey = 'iN1Tm7J7pbLNz39er';

    const templateParams = {
      to_email: 'sales@rkandkimport.com',
      message: `
        Client Name: ${name}\n
        Client Email: ${email}\n
        Subject: ${subject}\n
        Message: \n${message}
      `,
    };

    emailjs
      .send(serviceID, 'template_l1y5epa', templateParams, { publicKey })
      .then(() => {
        setStatusMessage('Message sent successfully! We\'ll get back to you soon.');
        setStatusOk(true);
        setIsSubmitting(false);
        localStorage.setItem('lastSubmissionTime', currentTime.toString());
        form.current!.reset();
        setSubject('');
      })
      .catch((error) => {
        console.error('FAILED...', error);
        setStatusMessage('Failed to send message. Please try again.');
        setStatusOk(false);
        setIsSubmitting(false);
      });
  };

  return (
    <motion.section
      ref={contactSectionRef}
      id="contact"
      className="min-h-screen text-black dark:text-[#E5E7EB] py-24 px-6 md:px-20 relative overflow-hidden bg-[#FBFBFB] dark:bg-[#121212] transition-colors duration-500"
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <motion.div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-[#172b81]/10 dark:bg-[#172b81]/20 blur-[120px] animate-pulse" style={{ y: contactBlobY1 }} />
        <motion.div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[#e1ae48]/15 dark:bg-[#e1ae48]/15 blur-[100px] animate-pulse delay-700" style={{ y: contactBlobY2 }} />
        <div className="absolute inset-0 opacity-60 dark:opacity-20 bg-[radial-gradient(#80808055_1px,transparent_1px)] bg-[size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"></div>

        {/* Floating accent shapes */}
        {!prefersReducedMotion && (
          <>
            <motion.div
              className="absolute top-[18%] left-[8%] w-3 h-3 rounded-full bg-[#e1ae48]/60"
              animate={{ y: [0, -18, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute top-[30%] right-[6%] w-4 h-4 rotate-45 border border-[#e1ae48]/40"
              animate={{ rotate: [45, 90, 45], y: [0, -10, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute bottom-[26%] left-[46%] w-2.5 h-2.5 rounded-full bg-[#172b81]/40 dark:bg-[#e1ae48]/40"
              animate={{ y: [0, -16, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
            />
          </>
        )}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* ---------- Left: heading + bento contact cards ---------- */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
          >
            <AnimatedText
              as={motion.h2}
              className="text-5xl md:text-6xl font-bold tracking-tight mb-4 text-slate-900 dark:text-white"
              text="Let's talk"
            />
            <p className="text-slate-500 dark:text-slate-400 mb-10 max-w-md text-lg leading-relaxed">
              We are always ready to help you and answer your questions. Reach out for inquiries, quotes, or support.
            </p>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
            >
              {/* Call center — hero card */}
              <motion.div
                variants={cardVariants}
                className="sm:col-span-2 relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-[#172b81] via-[#1d3597] to-[#0f1d5c] text-white shadow-xl shadow-[#172b81]/20"
              >
                <div className="absolute -bottom-16 -left-10 w-48 h-48 rounded-full bg-[#e1ae48]/20 blur-3xl" />
                <div className="relative flex items-center gap-3 mb-5">
                  <span className="relative w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Phone size={17} className="text-[#e1ae48]" />
                    {!prefersReducedMotion && <span className="absolute inset-0 rounded-full border border-[#e1ae48]/50 animate-ping" />}
                  </span>
                  <h3 className="text-[11px] font-black uppercase tracking-[0.25em] text-white/70">Call Center</h3>
                </div>
                <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {phoneLines.map((line) => (
                    <a
                      key={line.href}
                      href={line.href}
                      className="group flex sm:flex-col items-center sm:items-start justify-between gap-1 rounded-2xl px-4 py-3 bg-white/[0.07] hover:bg-[#e1ae48] border border-white/10 hover:border-transparent transition-all duration-300 hover:-translate-y-0.5"
                    >
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#e1ae48] group-hover:text-black/60 transition-colors">{line.dept}</span>
                      <span className="flex items-center gap-1 text-sm font-semibold group-hover:text-black transition-colors whitespace-nowrap">
                        {line.text}
                        <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </span>
                    </a>
                  ))}
                </div>
              </motion.div>

              {/* Email card */}
              <motion.div
                variants={cardVariants}
                className="relative overflow-hidden rounded-3xl p-6 bg-white/90 dark:bg-[#1E1E1E]/90 backdrop-blur-xl border border-slate-200/70 dark:border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
              >
                <Mail aria-hidden className="absolute -right-5 -bottom-5 w-28 h-28 text-[#172b81]/[0.05] dark:text-white/[0.04] rotate-12" />
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-[#172b81]/10 dark:bg-[#172b81]/25 flex items-center justify-center text-[#172b81] dark:text-[#a8c0ff]">
                    <Mail size={17} />
                  </span>
                  <h3 className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500">Email</h3>
                </div>
                <div className="relative space-y-1">
                  {emailLines.map((line) => (
                    <a
                      key={line.href}
                      href={line.href}
                      className="group flex items-center justify-between gap-2 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:text-[#172b81] dark:hover:text-[#e1ae48] transition-colors"
                    >
                      <span className="truncate">
                        <span className="font-semibold">{line.text.split("@")[0]}</span>
                        <span className="text-slate-400 dark:text-slate-500">@{line.text.split("@")[1]}</span>
                      </span>
                      <ArrowUpRight size={14} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  ))}
                </div>
              </motion.div>

              {/* Location card */}
              <motion.a
                variants={cardVariants}
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-3xl p-6 flex flex-col justify-between min-h-[190px] bg-[#e1ae48]/10 dark:bg-[#e1ae48]/[0.07] border border-[#e1ae48]/30 hover:border-[#e1ae48]/60 transition-colors"
              >
                {/* Stylised map lines */}
                <svg aria-hidden className="absolute inset-0 w-full h-full text-[#e1ae48]/30" preserveAspectRatio="none" viewBox="0 0 200 200">
                  <path d="M0 60 L200 90 M0 140 L200 120 M60 0 L80 200 M150 0 L130 200" stroke="currentColor" strokeWidth="1.2" fill="none" />
                  <path d="M0 175 Q 100 150 200 175" stroke="currentColor" strokeWidth="6" fill="none" opacity="0.4" />
                </svg>
                <div className="absolute top-[38%] left-[62%] -translate-x-1/2 -translate-y-1/2">
                  {!prefersReducedMotion && <span className="absolute inset-0 -m-2 rounded-full bg-[#e1ae48]/40 animate-ping" />}
                  <span className="relative w-9 h-9 rounded-full bg-[#e1ae48] text-black flex items-center justify-center shadow-lg shadow-[#e1ae48]/40 transition-transform duration-300 group-hover:-translate-y-1">
                    <MapPin size={17} />
                  </span>
                </div>
                <h3 className="relative text-[11px] font-black uppercase tracking-[0.25em] text-[#9a7424] dark:text-[#e1ae48]/80">Our Location</h3>
                <div className="relative">
                  <p className="font-semibold text-slate-900 dark:text-white leading-snug">{ADDRESS}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#172b81] dark:text-[#e1ae48]">
                    Open in Maps
                    <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </motion.a>
            </motion.div>
          </motion.div>

          {/* ---------- Right: form with animated gradient border ---------- */}
          <motion.div
            className="relative lg:sticky lg:top-24"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false }}
          >
            {/* Tilted backing card for depth */}
            <div className="absolute inset-0 rounded-[40px] bg-[#172b81] dark:bg-[#e1ae48]/80 rotate-[3deg] translate-x-2 translate-y-2 opacity-90" />

            <div className="relative rounded-[40px] p-[2px] overflow-hidden shadow-[0_30px_70px_rgba(23,43,129,0.15)] dark:shadow-none">
              <motion.div
                aria-hidden
                className="absolute inset-[-60%] bg-[conic-gradient(from_0deg,transparent_0deg,#e1ae48_70deg,transparent_140deg,#172b81_220deg,transparent_300deg)]"
                animate={prefersReducedMotion ? undefined : { rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />
              <div className="relative bg-white dark:bg-[#1E1E1E] p-8 md:p-10 rounded-[38px]">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-black dark:text-[#E5E7EB]">Contact Us</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Drop us a line and we'll get back to you soon.</p>
                  </div>
                  <motion.span
                    className="w-12 h-12 flex-shrink-0 rounded-2xl bg-[#e1ae48]/15 text-[#e1ae48] flex items-center justify-center"
                    animate={prefersReducedMotion ? undefined : { rotate: [0, 8, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Sparkles size={20} />
                  </motion.span>
                </div>

                <div className="mb-6">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500 mb-3">What can we help with?</p>
                  <div className="flex flex-wrap gap-2">
                    {topics.map((topic) => {
                      const active = subject === topic;
                      return (
                        <motion.button
                          key={topic}
                          type="button"
                          onClick={() => setSubject(active ? '' : topic)}
                          whileTap={{ scale: 0.94 }}
                          aria-pressed={active}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            active
                              ? 'bg-[#172b81] border-[#172b81] text-white dark:bg-[#e1ae48] dark:border-[#e1ae48] dark:text-black'
                              : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-[#172b81]/40 dark:hover:border-[#e1ae48]/40'
                          }`}
                        >
                          {topic}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                <form ref={form} className="space-y-4" onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FloatingField id="contact-name" name="user_name" label="Full Name" />
                    <FloatingField id="contact-email" name="user_email" label="Email" type="email" />
                  </div>
                  <FloatingField id="contact-subject" name="subject" label="Subject" value={subject} onChange={setSubject} />
                  <FloatingField id="contact-message" name="message" label="Message" textarea rows={5} />

                  <CreativeButton
                    type="submit"
                    disabled={isSubmitting}
                    fillColor="#e1ae48"
                    hoverTextClassName="text-black"
                    className="w-full py-4 font-bold tracking-wide bg-[#172b81] text-white shadow-lg shadow-[#172b81]/20 dark:bg-[#e1ae48] dark:text-black dark:shadow-none disabled:opacity-70"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send a message
                        <Send size={16} />
                      </>
                    )}
                  </CreativeButton>

                  <AnimatePresence mode="wait">
                    {statusMessage && (
                      <motion.p
                        key={statusMessage}
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8 }}
                        className={`flex items-center justify-center gap-2 text-sm mt-4 text-center font-medium ${statusOk === false ? 'text-red-500' : statusOk === true ? 'text-green-500' : 'text-slate-500 dark:text-slate-400'}`}
                      >
                        {statusOk === true && <CheckCircle2 size={16} />}
                        {statusOk === false && <XCircle size={16} />}
                        {statusMessage}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default ContactSection;
